export type OrderMode = 'strict_pay_first' | 'open_table' | 'staff_unlocked' | 'custom'

export type CustomerPaymentTiming = 'before_sending' | 'after_sending' | 'either'

export type UnpaidProcessingPolicy = 'blocked' | 'warning' | 'allowed'

export type TableQrBehavior = 'open_directly' | 'requires_unlock' | 'pay_first_to_unlock'

export type PrintedQrBehavior = 'disabled' | 'temporary_open_session' | 'unlock_token'

export type PosChimeTrigger = 'order_received' | 'payment_received' | 'both'

export interface OrderModeSettings {
  orderMode: OrderMode
  customerPaymentTiming: CustomerPaymentTiming
  unpaidProcessingPolicy: UnpaidProcessingPolicy
  tableQrBehavior: TableQrBehavior
  printedQrBehavior: PrintedQrBehavior
  staffManualUnlockEnabled: boolean
  pinUnlockEnabled: boolean
  minimumUnlockPaymentEnabled: boolean
  minimumUnlockPaymentAmount: number | null
  posChimeTrigger: PosChimeTrigger
  unpaidOrderTimeoutMinutes: number | null
  autoAdvancePaidOrdersToProcessing: boolean
  /** After an order is saved, decrement tracked remaining. Off by default. */
  itemStockDeductEnabled: boolean
  /** Minutes of quiet before an unsent cart drops and a finished, paid seating leaves the guest phone. 0 = off. */
  tableSeatingIdleMinutes: number
}

export type OrderModePreset = Exclude<OrderMode, 'custom'>

export interface OrderModeOrderLike {
  status?: string
}

export interface OrderModeBillLike {
  paidAt?: string | null
}

export interface TableAccessState {
  customerOrderingLocked?: boolean
}

export type ProcessingDecision =
  | { allowed: true; requiresWarning: false; reason?: undefined }
  | { allowed: true; requiresWarning: true; reason: string }
  | { allowed: false; requiresWarning: false; reason: string }

export const DEFAULT_ORDER_MODE_SETTINGS: OrderModeSettings = {
  orderMode: 'open_table',
  customerPaymentTiming: 'after_sending',
  unpaidProcessingPolicy: 'warning',
  tableQrBehavior: 'open_directly',
  printedQrBehavior: 'disabled',
  staffManualUnlockEnabled: false,
  pinUnlockEnabled: false,
  minimumUnlockPaymentEnabled: false,
  minimumUnlockPaymentAmount: null,
  posChimeTrigger: 'order_received',
  unpaidOrderTimeoutMinutes: null,
  autoAdvancePaidOrdersToProcessing: false,
  itemStockDeductEnabled: false,
  tableSeatingIdleMinutes: 15,
}

const ORDER_MODES: OrderMode[] = ['strict_pay_first', 'open_table', 'staff_unlocked', 'custom']
const CUSTOMER_PAYMENT_TIMINGS: CustomerPaymentTiming[] = ['before_sending', 'after_sending', 'either']
const UNPAID_PROCESSING_POLICIES: UnpaidProcessingPolicy[] = ['blocked', 'warning', 'allowed']
const TABLE_QR_BEHAVIORS: TableQrBehavior[] = ['open_directly', 'requires_unlock', 'pay_first_to_unlock']
const PRINTED_QR_BEHAVIORS: PrintedQrBehavior[] = ['disabled', 'temporary_open_session', 'unlock_token']
const POS_CHIME_TRIGGERS: PosChimeTrigger[] = ['order_received', 'payment_received', 'both']

export function applyOrderModePreset(
  mode: OrderModePreset,
  current: Partial<OrderModeSettings> | null | undefined,
): OrderModeSettings {
  const base = mergeOrderModeSettings(current)
  switch (mode) {
    case 'strict_pay_first':
      return {
        ...base,
        orderMode: mode,
        customerPaymentTiming: 'before_sending',
        unpaidProcessingPolicy: 'blocked',
        tableQrBehavior: 'open_directly',
        posChimeTrigger: 'payment_received',
        autoAdvancePaidOrdersToProcessing: true,
      }
    case 'open_table':
      return {
        ...base,
        orderMode: mode,
        customerPaymentTiming: 'after_sending',
        unpaidProcessingPolicy: 'warning',
        tableQrBehavior: 'open_directly',
        staffManualUnlockEnabled: false,
        posChimeTrigger: 'order_received',
        autoAdvancePaidOrdersToProcessing: false,
      }
    case 'staff_unlocked':
      return {
        ...base,
        orderMode: mode,
        customerPaymentTiming: 'after_sending',
        unpaidProcessingPolicy: 'warning',
        tableQrBehavior: 'requires_unlock',
        staffManualUnlockEnabled: true,
        posChimeTrigger: 'order_received',
        autoAdvancePaidOrdersToProcessing: false,
      }
  }
}

function enumOrDefault<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && allowed.includes(value as T) ? (value as T) : fallback
}

function booleanOrDefault(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function nullableNonNegativeNumber(value: unknown): number | null {
  if (value == null) return null
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return Math.max(0, Math.round(value))
}

export function mergeOrderModeSettings(
  partial: Partial<OrderModeSettings> | null | undefined,
): OrderModeSettings {
  if (!partial) return { ...DEFAULT_ORDER_MODE_SETTINGS }
  return {
    orderMode: enumOrDefault(partial.orderMode, ORDER_MODES, DEFAULT_ORDER_MODE_SETTINGS.orderMode),
    customerPaymentTiming: enumOrDefault(
      partial.customerPaymentTiming,
      CUSTOMER_PAYMENT_TIMINGS,
      DEFAULT_ORDER_MODE_SETTINGS.customerPaymentTiming,
    ),
    unpaidProcessingPolicy: enumOrDefault(
      partial.unpaidProcessingPolicy,
      UNPAID_PROCESSING_POLICIES,
      DEFAULT_ORDER_MODE_SETTINGS.unpaidProcessingPolicy,
    ),
    tableQrBehavior: enumOrDefault(
      partial.tableQrBehavior,
      TABLE_QR_BEHAVIORS,
      DEFAULT_ORDER_MODE_SETTINGS.tableQrBehavior,
    ),
    printedQrBehavior: enumOrDefault(
      partial.printedQrBehavior,
      PRINTED_QR_BEHAVIORS,
      DEFAULT_ORDER_MODE_SETTINGS.printedQrBehavior,
    ),
    staffManualUnlockEnabled: booleanOrDefault(
      partial.staffManualUnlockEnabled,
      DEFAULT_ORDER_MODE_SETTINGS.staffManualUnlockEnabled,
    ),
    pinUnlockEnabled: booleanOrDefault(
      partial.pinUnlockEnabled,
      DEFAULT_ORDER_MODE_SETTINGS.pinUnlockEnabled,
    ),
    minimumUnlockPaymentEnabled: booleanOrDefault(
      partial.minimumUnlockPaymentEnabled,
      DEFAULT_ORDER_MODE_SETTINGS.minimumUnlockPaymentEnabled,
    ),
    minimumUnlockPaymentAmount: nullableNonNegativeNumber(partial.minimumUnlockPaymentAmount),
    posChimeTrigger: enumOrDefault(
      partial.posChimeTrigger,
      POS_CHIME_TRIGGERS,
      DEFAULT_ORDER_MODE_SETTINGS.posChimeTrigger,
    ),
    unpaidOrderTimeoutMinutes: nullableNonNegativeNumber(partial.unpaidOrderTimeoutMinutes),
    autoAdvancePaidOrdersToProcessing: booleanOrDefault(
      partial.autoAdvancePaidOrdersToProcessing,
      DEFAULT_ORDER_MODE_SETTINGS.autoAdvancePaidOrdersToProcessing,
    ),
    itemStockDeductEnabled: booleanOrDefault(
      partial.itemStockDeductEnabled,
      DEFAULT_ORDER_MODE_SETTINGS.itemStockDeductEnabled,
    ),
    tableSeatingIdleMinutes: nonNegativeNumber(
      partial.tableSeatingIdleMinutes,
      DEFAULT_ORDER_MODE_SETTINGS.tableSeatingIdleMinutes,
    ),
  }
}

function nonNegativeNumber(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.max(0, Math.round(value))
}

function settingsEqual(a: OrderModeSettings, b: OrderModeSettings): boolean {
  return (
    a.customerPaymentTiming === b.customerPaymentTiming &&
    a.unpaidProcessingPolicy === b.unpaidProcessingPolicy &&
    a.tableQrBehavior === b.tableQrBehavior &&
    a.printedQrBehavior === b.printedQrBehavior &&
    a.staffManualUnlockEnabled === b.staffManualUnlockEnabled &&
    a.pinUnlockEnabled === b.pinUnlockEnabled &&
    a.minimumUnlockPaymentEnabled === b.minimumUnlockPaymentEnabled &&
    a.minimumUnlockPaymentAmount === b.minimumUnlockPaymentAmount &&
    a.posChimeTrigger === b.posChimeTrigger &&
    a.unpaidOrderTimeoutMinutes === b.unpaidOrderTimeoutMinutes &&
    a.autoAdvancePaidOrdersToProcessing === b.autoAdvancePaidOrdersToProcessing
  )
}

export function resolveOrderModePreset(settings: OrderModeSettings): OrderMode {
  const normalized = mergeOrderModeSettings(settings)
  for (const mode of ['strict_pay_first', 'open_table', 'staff_unlocked'] as const) {
    if (settingsEqual(normalized, applyOrderModePreset(mode, normalized))) return mode
  }
  return 'custom'
}

export function isBillPaidForOrderMode(bill: OrderModeBillLike | null | undefined): boolean {
  return bill?.paidAt != null
}

export function canMoveOrderToProcessing(
  order: OrderModeOrderLike,
  bill: OrderModeBillLike | null | undefined,
  settings: OrderModeSettings,
): ProcessingDecision {
  if (order.status !== 'pending' && order.status !== 'submitted') {
    return { allowed: true, requiresWarning: false }
  }
  if (isBillPaidForOrderMode(bill)) return { allowed: true, requiresWarning: false }
  switch (settings.unpaidProcessingPolicy) {
    case 'blocked':
      return {
        allowed: false,
        requiresWarning: false,
        reason: 'This order is unpaid. Take payment before moving it to processing.',
      }
    case 'warning':
      return {
        allowed: true,
        requiresWarning: true,
        reason: 'This order is unpaid. Confirm only if staff intentionally wants to process it before payment.',
      }
    case 'allowed':
      return { allowed: true, requiresWarning: false }
  }
}

export function canCustomerSendOrder(
  settings: OrderModeSettings,
  tableAccessState: TableAccessState | null | undefined,
): boolean {
  if (settings.tableQrBehavior === 'open_directly') return true
  if (settings.tableQrBehavior === 'requires_unlock') {
    return tableAccessState?.customerOrderingLocked === false
  }
  if (settings.tableQrBehavior === 'pay_first_to_unlock') {
    return tableAccessState?.customerOrderingLocked === false
  }
  return true
}

export function getInitialCustomerOrderStatus(settings: OrderModeSettings): 'pending' | 'submitted' {
  return settings.customerPaymentTiming === 'before_sending' ? 'submitted' : 'pending'
}

export function shouldChimeForEvent(
  event: 'order_received' | 'payment_received',
  settings: OrderModeSettings,
): boolean {
  return settings.posChimeTrigger === 'both' || settings.posChimeTrigger === event
}

export function toPublicOrderModeSettings(settings: OrderModeSettings): OrderModeSettings {
  return mergeOrderModeSettings(settings)
}
